import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Building2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const ep = mode === "login" ? "/auth/login" : "/auth/register";
      const { data } = await api.post(ep, form);
      setUser(data);
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden text-white" style={{ background: "linear-gradient(150deg, #0f172a 0%, #1e3a8a 60%, #047857 130%)" }}>
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center">
            <span className="font-heading font-extrabold">K</span>
          </div>
          <span className="font-heading font-bold text-lg tracking-tight">Kora Holding, LLC</span>
        </div>
        <div className="space-y-4">
          <h1 className="font-heading text-4xl font-bold leading-tight">Real Estate Portfolio<br />Management.</h1>
          <p className="text-white/70 max-w-md">Kora Holding, LLC centralizes properties, units, tenants, leases, rent, taxes, insurance, mortgages, maintenance and financials — so you never miss a deadline.</p>
        </div>
        <p className="text-xs text-white/50 tracking-widest uppercase">Real Estate Portfolio Management</p>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm space-y-6">
          <div className="lg:hidden flex items-center gap-2.5 mb-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center text-white">
              <span className="font-heading font-extrabold">K</span>
            </div>
            <span className="font-heading font-bold text-lg tracking-tight">Kora Holding, LLC</span>
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold">{mode === "login" ? "Sign in" : "Create account"}</h2>
            <p className="text-sm text-muted-foreground mt-1">Access your real estate portfolio</p>
          </div>
          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <>
                <Input data-testid="name-input" placeholder="Full Name" className="rounded-xl" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                <p className="text-xs text-muted-foreground border border-border bg-muted/40 px-3 py-2 rounded-xl">New accounts require <span className="font-medium">administrator approval</span> before portfolio access is granted.</p>
              </>
            )}
            <Input data-testid="email-input" type="email" placeholder="Email" className="rounded-xl" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            <Input data-testid="password-input" type="password" placeholder="Password" className="rounded-xl" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
            <Button type="submit" className="w-full rounded-xl" disabled={loading} data-testid="submit-button">
              {loading ? "Please wait..." : mode === "login" ? "Sign In" : "Register"}
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-background px-2 text-muted-foreground">or</span></div>
          </div>

          <Button variant="outline" className="w-full rounded-xl" onClick={googleLogin} data-testid="google-login-button">
            Continue with Google
          </Button>

          <p className="text-sm text-center text-muted-foreground">
            {mode === "login" ? "No account?" : "Have an account?"}{" "}
            <button className="text-primary font-medium" onClick={() => setMode(mode === "login" ? "register" : "login")} data-testid="toggle-mode">
              {mode === "login" ? "Register" : "Sign in"}
            </button>
          </p>
          <p className="text-xs text-center text-muted-foreground border-t border-border pt-4">
            Demo: admin@portfolio.com / Estate0ps!Secure#2026
          </p>
        </div>
      </div>
    </div>
  );
}
